import React, { useState } from "react";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import MenuItem from "@mui/material/MenuItem";
import MUISelect from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";

interface SelectProps {
  options: { value: string | number; label: string; testId?: string; disabled?: boolean }[];
  value?: string;
  testId: string;
  error?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onClose?: () => void;
  placeholder?: string;
  style?: React.CSSProperties;
  dir?: "ltr" | "rtl";
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  fullWidth?: boolean;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  testId,
  error,
  onChange,
  onBlur,
  onClose,
  placeholder,
  style,
  dir = "ltr",
  inputRef,
  disabled = false,
  fullWidth = false,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <MUISelect
      MenuProps={{ disablePortal: true }}
      data-testid={testId}
      value={value !== undefined ? String(value) : ""}
      IconComponent={KeyboardArrowDownOutlinedIcon}
      error={error}
      onChange={(event: SelectChangeEvent) => {
        onChange(event.target.value);
      }}
      disabled={disabled}
      fullWidth={fullWidth}
      displayEmpty
      renderValue={(selected) => {
        if (!selected) {
          return <span style={{ color: theme.palette.text.placeholder }}>{placeholder}</span>;
        }

        const found = options.find((opt) => String(opt.value) === String(selected));
        return <span className="capitalize">{found?.label || selected}</span>;
      }}
      open={open}
      dir={dir}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        onBlur?.();
        onClose?.();
      }}
      style={style}
      inputRef={inputRef}
    >
      {options.map((option, index) => (
        <MenuItem
          key={`${option.value}-${index}`}
          value={option.value}
          data-testid={option.testId ?? option.value}
          disabled={option.disabled}
          dir={dir}
          sx={{
            "&:hover": {
              backgroundColor: "action.selected",
              color: "text.black",
            },
            "&.Mui-selected": {
              backgroundColor: "action.selected",
              color: "text.black",
            },
          }}
        >
          <span className="capitalize" dir={dir}>
            {option.label}
          </span>
        </MenuItem>
      ))}
    </MUISelect>
  );
};

export default Select;
